import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Loader2,
  FileText,
  Award,
  BarChart3,
  RefreshCw,
  TrendingUp,
  AlertTriangle,
  PieChart,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useInvestorPortal } from '@/hooks/useInvestorPortal';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { NotificationBell } from '@/components/investor/NotificationBell';
import { PortfolioSummaryWidget } from '@/components/investor/PortfolioSummaryWidget';
import { InvestorErrorBoundary } from '@/components/investor/InvestorErrorBoundary';
import { InvestorMessages } from '@/components/investor/InvestorMessages';
import { MessageInsights } from '@/components/investor/MessageInsights';
import { SubscriptionCapTablePreview } from '@/components/investor/SubscriptionCapTablePreview';
import { OwnershipHistoryTimeline } from '@/components/investor/OwnershipHistoryTimeline';
import { OwnershipProjectionModel } from '@/components/investor/OwnershipProjectionModel';
import { AntiDilutionCalculator } from '@/components/investor/AntiDilutionCalculator';
import { PayToPlayCalculator } from '@/components/investor/PayToPlayCalculator';
import { RightsComparisonMatrix } from '@/components/investor/RightsComparisonMatrix';
import { ExitWaterfallCalculator } from '@/components/investor/ExitWaterfallCalculator';
import { SafeConversionSimulator } from '@/components/investor/SafeConversionSimulator';
import { ConvertibleNoteSimulator } from '@/components/investor/ConvertibleNoteSimulator';
import { TermSheetComparison } from '@/components/investor/TermSheetComparison';
import CapTableSensitivityAnalyzer from '@/components/investor/CapTableSensitivityAnalyzer';
import OptionPoolPlanner from '@/components/investor/OptionPoolPlanner';
import LiquidationWaterfallVisualizer from '@/components/investor/LiquidationWaterfallVisualizer';
import DragAlongCalculator from '@/components/investor/DragAlongCalculator';
import ROFRSimulator from '@/components/investor/ROFRSimulator';
import VestingScheduleSimulator from '@/components/investor/VestingScheduleSimulator';
import FounderDilutionTracker from '@/components/investor/FounderDilutionTracker';
import OptionPoolImpactAnalyzer from '@/components/investor/OptionPoolImpactAnalyzer';
import FounderEquitySplitCalculator from '@/components/investor/FounderEquitySplitCalculator';
import SecondaryMarketplace from '@/components/secondary/SecondaryMarketplace';

interface CompanyData {
  company_id: string;
  company_name: string;
}

export default function InvestorPortal() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use consolidated data fetching hook
  const {
    holdings,
    certificates,
    valuations,
    reports,
    messages,
    portfolioSummary,
    loading: dataLoading,
    error: dataError,
    refresh,
  } = useInvestorPortal();

  // Staging mode enabled by default - set to false for production
  const isStaging = true;

  async function load() {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      if (isStaging) {
        // STAGING MODE: Show all companies
        const { data: allCompanies, error } = await supabase
          .from('companies')
          .select('id, name')
          .order('name');

        if (error) throw error;

        setCompanies(
          allCompanies?.map((c) => ({
            company_id: c.id,
            company_name: c.name,
          })) || []
        );
      } else {
        // PRODUCTION: Only show investor-linked companies
        const { data: identities, error } = await supabase
          .from('investor_identities')
          .select(
            `
            shareholder_id,
            shareholders!inner(
              company_id,
              companies!inner(
                id,
                name
              )
            )
          `
          )
          .eq('user_id', user.id);

        if (error) throw error;

        // Extract unique companies
        const uniqueCompanies = new Map<string, CompanyData>();
        identities?.forEach((identity: any) => {
          const companyId = identity.shareholders?.companies?.id;
          const companyName = identity.shareholders?.companies?.name;
          if (companyId && companyName && !uniqueCompanies.has(companyId)) {
            uniqueCompanies.set(companyId, {
              company_id: companyId,
              company_name: companyName,
            });
          }
        });

        setCompanies(Array.from(uniqueCompanies.values()));
      }
    } catch (e: any) {
      const errorMsg = e.message || 'Failed to load investor data';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Error loading investor portal:', e);
    } finally {
      setLoading(false);
    }
  }

  const handleRefresh = () => {
    // Only call refresh() as it reloads investor data via useInvestorPortal
    // load() only fetches company list which doesn't need refreshing on every click
    refresh();
  };

  useEffect(() => {
    if (user) {
      load();
    }
  }, [user?.id]);

  if (authLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center p-8">
          <p className="text-muted-foreground">Please log in to view your investor portal.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">My Investments</h1>
              {isStaging && (
                <Badge variant="destructive" className="gap-1.5">
                  <AlertTriangle className="h-3 w-3" />
                  STAGING MODE
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1">
              {isStaging
                ? 'Showing all companies (staging mode enabled)'
                : 'View your holdings, certificates, and investor reports'}
            </p>
          </div>
          <div className="flex gap-2">
            <InvestorErrorBoundary>
              <NotificationBell />
            </InvestorErrorBoundary>
            <Button variant="outline" onClick={() => navigate('/investor-portal/portfolio')}>
              <PieChart className="h-4 w-4 mr-2" />
              Portfolio Analytics
            </Button>
            <Button variant="outline" onClick={handleRefresh} disabled={loading || dataLoading}>
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loading || dataLoading ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          </div>
        </div>

        {/* Portfolio Summary Widget */}
        <InvestorErrorBoundary>
          <PortfolioSummaryWidget summary={portfolioSummary} loading={dataLoading} />
        </InvestorErrorBoundary>

        {/* Subscription Cap Table Preview */}
        {companies.length > 0 && (
          <InvestorErrorBoundary>
            {companies.map((company) => (
              <SubscriptionCapTablePreview
                key={company.company_id}
                companyId={company.company_id}
              />
            ))}
          </InvestorErrorBoundary>
        )}

        {loading ? (
          <CompanyCardSkeleton />
        ) : error ? (
          <Card className="border-destructive/50">
            <CardContent className="py-8">
              <div className="text-center space-y-4">
                <AlertTriangle className="h-12 w-12 mx-auto text-destructive" />
                <p className="text-muted-foreground">{error}</p>
                <Button onClick={handleRefresh} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : companies.length ? (
          companies.map((company) => (
            <Card key={company.company_id} className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-xl">{company.company_name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Term Sheet Comparison */}
                <TermSheetComparison />

                {/* Cap Table Sensitivity Analyzer */}
                <CapTableSensitivityAnalyzer />

                {/* Option Pool Planner */}
                <OptionPoolPlanner />

                {/* Liquidation Waterfall Visualizer */}
                <LiquidationWaterfallVisualizer />

                {/* Drag-Along Rights Calculator */}
                <DragAlongCalculator />

                {/* ROFR Simulator */}
                <ROFRSimulator />

                {/* Vesting Schedule Simulator */}
                <VestingScheduleSimulator />

                {/* Option Pool Impact Analyzer */}
                <OptionPoolImpactAnalyzer />

                {/* Founder Equity Split Calculator */}
                <FounderEquitySplitCalculator />

                {/* Founder Dilution Tracker */}
                <FounderDilutionTracker />

                {/* Convertible Note Simulator */}
                <ConvertibleNoteSimulator />

                {/* SAFE Conversion Simulator */}
                <SafeConversionSimulator />

                {/* Exit Waterfall Calculator */}
                <ExitWaterfallCalculator />

                {/* Pay-to-Play Calculator */}
                <PayToPlayCalculator />

                {/* Rights Comparison Matrix */}
                <RightsComparisonMatrix />

                {/* Anti-Dilution Calculator */}
                <AntiDilutionCalculator />

                {/* Ownership Projection Model */}
                {holdings.filter((h) => h.company_id === company.company_id).length > 0 && (
                  <OwnershipProjectionModel
                    companyId={company.company_id}
                    shareholderId={
                      holdings.find((h) => h.company_id === company.company_id)?.shareholder_id ||
                      ''
                    }
                    currentOwnership={(() => {
                      const myHoldings = holdings.filter(
                        (h) => h.company_id === company.company_id
                      );
                      const myShares = myHoldings.reduce((sum, h) => sum + (h.shares || 0), 0);
                      const valuation = valuations.find((v) => v.company_id === company.company_id);
                      const totalShares = valuation?.total_shares || 0;
                      return totalShares > 0 ? (myShares / totalShares) * 100 : 0;
                    })()}
                  />
                )}

                {/* Ownership History Timeline */}
                {holdings.filter((h) => h.company_id === company.company_id).length > 0 && (
                  <OwnershipHistoryTimeline
                    companyId={company.company_id}
                    shareholderId={
                      holdings.find((h) => h.company_id === company.company_id)?.shareholder_id ||
                      ''
                    }
                  />
                )}

                <Holdings
                  data={holdings.filter((h) => h.company_id === company.company_id)}
                  loading={dataLoading}
                />
                <Certificates
                  data={certificates.filter((c) => c.company_id === company.company_id)}
                  loading={dataLoading}
                />
                <Valuation
                  data={valuations.find((v) => v.company_id === company.company_id)}
                  loading={dataLoading}
                />
                <Reports reports={reports.get(company.company_id) || []} loading={dataLoading} />
                <div className="space-y-6">
                  <SecondaryMarketplace companyId={company.company_id} />
                  <InvestorMessages
                    companyId={company.company_id}
                    messages={messages.get(company.company_id) || []}
                    onRefresh={handleRefresh}
                  />
                  <MessageInsights companyId={company.company_id} />
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="py-8">
              <div className="text-center space-y-2">
                <p className="text-muted-foreground">
                  You don't have any investor data linked to your account yet.
                </p>
                <p className="text-sm text-muted-foreground">
                  Please contact your company administrator to link your account to your shareholder
                  identity.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

// Skeleton component
function CompanyCardSkeleton() {
  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </CardContent>
    </Card>
  );
}

// Holdings subcomponent - now receives data as props
function Holdings({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Holdings
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : data.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shareholder</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead className="text-right">Shares</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((r, i) => (
                  <TableRow key={`${r.shareholder_id}-${r.share_class}-${i}`}>
                    <TableCell className="font-medium">{r.shareholder_name || '—'}</TableCell>
                    <TableCell>{r.share_class || '—'}</TableCell>
                    <TableCell className="text-right">{r.shares?.toLocaleString() || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No holdings data available</p>
        )}
      </CardContent>
    </Card>
  );
}

// Certificates subcomponent - now receives data as props
function Certificates({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Award className="h-4 w-4" />
          Certificates
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : data.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Shares</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issued</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((r) => (
                  <TableRow key={r.certificate_id}>
                    <TableCell className="font-medium">{r.cert_number || '—'}</TableCell>
                    <TableCell>{r.share_class || '—'}</TableCell>
                    <TableCell>{r.shares?.toLocaleString() || 0}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          r.status === 'ACTIVE'
                            ? 'bg-success/10 text-success'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {r.status || 'UNKNOWN'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {r.issue_date ? new Date(r.issue_date).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No certificates available</p>
        )}
      </CardContent>
    </Card>
  );
}

// Valuation subcomponent - now receives data as props
function Valuation({ data, loading }: { data: any | undefined; loading: boolean }) {
  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Valuation Summary
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : data ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Shares</p>
              <p className="text-lg font-semibold">{data.total_shares?.toLocaleString() || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">FMV per Share</p>
              <p className="text-lg font-semibold">
                {data.fmv_per_share ? `R${data.fmv_per_share.toFixed(4)}` : '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">As of Date</p>
              <p className="text-lg font-semibold">
                {data.as_of_date ? new Date(data.as_of_date).toLocaleDateString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Implied Value</p>
              <p className="text-lg font-semibold text-primary">
                {data.implied_value ? `R${data.implied_value.toLocaleString()}` : '—'}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No valuation data available</p>
        )}
      </CardContent>
    </Card>
  );
}

// Reports subcomponent - now receives data as props
function Reports({ reports, loading }: { reports: any[]; loading: boolean }) {
  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Investor Reports
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : reports.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Download</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.period_start && r.period_end
                        ? `${new Date(r.period_start).toLocaleDateString()} → ${new Date(r.period_end).toLocaleDateString()}`
                        : '—'}
                    </TableCell>
                    <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {r.report_url ? (
                        <a
                          href={r.report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Open Report
                        </a>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No reports available yet</p>
        )}
      </CardContent>
    </Card>
  );
}
